import { useRef, useState } from 'react';
import { Field } from './Form.js';

/** A narrower accept list than PhotoUpload.tsx's — this section is photos only, no PDFs. */
const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp,image/heic';
const MAX_BYTES = 25 * 1024 * 1024;

export interface QueuedPhoto {
  id: string;
  file: File;
  previewUrl: string;
}

interface PropertyPhotoQueueProps {
  queue: QueuedPhoto[];
  primaryId: string | null;
  onChange: (queue: QueuedPhoto[], primaryId: string | null) => void;
}

/**
 * Photos picked before the property exists.
 *
 * Nothing uploads here — there is no propertyId to attach to yet, the same
 * constraint PhotoUpload.tsx's `parent` prop enforces everywhere else in the
 * app. Files sit in memory as object URLs; PropertyForm.tsx uploads the
 * queue and sets the chosen primary only once the property is actually
 * created.
 */
export function PropertyPhotoQueue({ queue, primaryId, onChange }: PropertyPhotoQueueProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  function addFile(file: File) {
    setError(null);
    if (file.size > MAX_BYTES) {
      setError('That file is too large. The limit is 25MB.');
      return;
    }
    if (!ACCEPT_IMAGES.split(',').includes(file.type)) {
      setError(`${file.type || 'That file type'} is not accepted. Use a JPEG, PNG, WebP or HEIC.`);
      return;
    }

    const item: QueuedPhoto = {
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    };
    // The first photo added becomes primary by default — still changeable below.
    onChange([...queue, item], primaryId ?? item.id);
    if (inputRef.current) inputRef.current.value = '';
  }

  function remove(id: string) {
    const removed = queue.find((photo) => photo.id === id);
    if (removed) URL.revokeObjectURL(removed.previewUrl);
    const next = queue.filter((photo) => photo.id !== id);
    onChange(next, primaryId === id ? (next[0]?.id ?? null) : primaryId);
  }

  return (
    <div className="card stack">
      <p className="form-section-label">Photos</p>
      <p className="muted">
        Uploaded once the property is created. Pick which one is primary — it becomes the thumbnail
        and hero image on the property's page.
      </p>

      {queue.length > 0 ? (
        <div className="photo-queue">
          {queue.map((photo) => (
            <div key={photo.id} className="photo-queue__item">
              <img src={photo.previewUrl} alt={photo.file.name} className="photo-queue__thumb" />
              <div className="photo-queue__controls">
                <label className="photo-queue__primary">
                  <input
                    type="radio"
                    name="primaryPhoto"
                    checked={primaryId === photo.id}
                    onChange={() => onChange(queue, photo.id)}
                  />
                  Primary
                </label>
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() => remove(photo.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <Field label="Add a photo" name="propertyPhoto" hint="JPEG, PNG, WebP or HEIC · up to 25MB">
        <input
          ref={inputRef}
          id="propertyPhoto"
          name="propertyPhoto"
          className="field__input"
          type="file"
          accept={ACCEPT_IMAGES}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) addFile(file);
          }}
        />
      </Field>

      {error ? <p className="alert alert--error">{error}</p> : null}
    </div>
  );
}
